import {Config, User} from "../utils/types";
import API from "../helpers/network/http";

type IntercomSettings = {
    app_id: string;
    api_base: string;
    [key: string]: unknown;
};

type IntercomFunction = {
    (...args: unknown[]): void;
    q?: unknown[][];
    c?: (args: unknown[]) => void;
};

type SupportConfig = Partial<Config> & {
    supportMode?: string;
    intercomAppId?: string;
    intercomApiBase?: string;
};

export type IntercomMessengerToken = {
    intercom_user_jwt: string;
    expires_in?: number;
    user_id?: string;
};

declare global {
    interface Window {
        Intercom?: IntercomFunction;
        intercomSettings?: IntercomSettings;
    }
}

const DEFAULT_INTERCOM_APP_ID = "t9s5dkhi";
const DEFAULT_INTERCOM_API_BASE = "https://api-iam.intercom.io";
const DEFAULT_SUPPORT_MODE = "intercom";

let booted = false;

const getString = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const compactSettings = <T extends Record<string, unknown>>(settings: T) => {
    const result: Partial<T> = {};

    Object.keys(settings).forEach((key) => {
        const value = settings[key];
        if (value !== undefined && value !== null && value !== "") {
            result[key as keyof T] = value as T[keyof T];
        }
    });

    return result;
};

export function getSupportMode(config?: SupportConfig | null) {
    return (
        getString(config?.support_mode) ||
        getString(config?.supportMode) ||
        getString(process.env.REACT_APP_SUPPORT_MODE) ||
        DEFAULT_SUPPORT_MODE
    ).toLowerCase();
}

export function getIntercomAppId(config?: SupportConfig | null) {
    return (
        getString(config?.intercom_app_id) ||
        getString(config?.intercomAppId) ||
        getString(process.env.REACT_APP_INTERCOM_APP_ID) ||
        DEFAULT_INTERCOM_APP_ID
    );
}

export function getIntercomApiBase(config?: SupportConfig | null) {
    return (
        getString(config?.intercom_api_base) ||
        getString(config?.intercomApiBase) ||
        getString(process.env.REACT_APP_INTERCOM_API_BASE) ||
        DEFAULT_INTERCOM_API_BASE
    );
}

export function isIntercomSupportEnabled(config?: SupportConfig | null) {
    return getSupportMode(config) === "intercom" && Boolean(getIntercomAppId(config));
}

function loadIntercom(appId: string) {
    if (typeof window === "undefined") return;

    const existingIntercom = window.Intercom;
    if (typeof existingIntercom === "function") {
        return;
    }

    const intercomQueue = ((...args: unknown[]) => {
        intercomQueue.c?.(args);
    }) as IntercomFunction;
    intercomQueue.q = [];
    intercomQueue.c = (args: unknown[]) => {
        intercomQueue.q?.push(args);
    };
    window.Intercom = intercomQueue;

    const insertScript = () => {
        if (document.querySelector(`script[src="https://widget.intercom.io/widget/${appId}"]`)) {
            return;
        }

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${appId}`;
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
    };

    if (document.readyState === "complete") {
        insertScript();
    } else {
        window.addEventListener("load", insertScript, false);
    }
}

export function bootIntercom(
    settings: Omit<IntercomSettings, "app_id" | "api_base"> = {},
    config?: SupportConfig | null
) {
    if (!isIntercomSupportEnabled(config) || typeof window === "undefined") return false;

    const appId = getIntercomAppId(config);
    const intercomSettings = {
        api_base: getIntercomApiBase(config),
        app_id: appId,
        ...settings,
    };

    window.intercomSettings = intercomSettings;
    loadIntercom(appId);

    if (booted) {
        window.Intercom?.("update", intercomSettings);
        return true;
    }

    window.Intercom?.("boot", intercomSettings);
    booted = true;
    return true;
}

export function updateIntercom(
    settings: Omit<IntercomSettings, "app_id" | "api_base"> = {},
    config?: SupportConfig | null
) {
    if (!isIntercomSupportEnabled(config) || typeof window === "undefined") return false;
    if (!booted) {
        return bootIntercom(settings, config);
    }

    window.Intercom?.("update", {
        api_base: getIntercomApiBase(config),
        app_id: getIntercomAppId(config),
        ...settings,
    });
    return true;
}

export function shutdownIntercom() {
    if (typeof window === "undefined") return;

    window.Intercom?.("shutdown");
    booted = false;
    delete window.intercomSettings;
}

export function openIntercomMessenger(
    config?: SupportConfig | null,
    settings: Omit<IntercomSettings, "app_id" | "api_base"> = {}
) {
    if (!isIntercomSupportEnabled(config) || typeof window === "undefined") return false;

    if (!booted) {
        if (!settings.intercom_user_jwt) return false;
        bootIntercom(settings, config);
    }

    window.Intercom?.("show");
    return true;
}

const toUnixSeconds = (value?: string | null) => {
    if (!value) return undefined;

    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) return undefined;

    return Math.floor(timestamp / 1000);
};

export function buildIntercomUserSettings(user: User) {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim() || user.username || user.email;

    return compactSettings({
        user_id: user.uuid || user.id || user.email,
        email: user.email,
        name,
        created_at: toUnixSeconds(user.created_at || user.createdAt),
        username: user.username,
        role: user.role,
        country: user.country,
        is_premium: user.is_premium,
        plan_type: user.plan_type,
    });
}

export async function fetchIntercomMessengerToken(): Promise<IntercomMessengerToken> {
    const {data} = await API.get("/intercom/messenger-token");
    return data.data;
}

export function buildIntercomJwtSettings(token: IntercomMessengerToken | null) {
    if (!token?.intercom_user_jwt) return null;

    return {
        intercom_user_jwt: token.intercom_user_jwt,
    };
}
